import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/zera/Navbar';
import PageHero from '@/components/zera/PageHero';
import Footer from '@/components/zera/Footer';
import ZeraPartners from '@/components/zera/ZeraPartners';
import CuratedItineraries from '@/components/zera/romania/CuratedItineraries';
import { albaniaItineraries, ALBANIA_BOOKING_LINK } from '@/data/albaniaItineraries';
import { Sun, Mountain, Waves, Utensils, Landmark, Heart, ArrowRight, Star } from 'lucide-react';


const highlights = [
  {
    icon: Waves,
    title: 'The Albanian Riviera',
    description: 'Over 450km of pristine coastline along the Adriatic and Ionian seas, with crystal-clear waters rivaling the Caribbean. Hidden coves, dramatic cliffs, and beaches that remain Europe\'s best-kept secret.',
    image: 'https://d64gsuwffb70l.cloudfront.net/69cbf6b38dc4f4af5d55476d_1776963107484_b7256427.png',
    alt: 'Albanian Riviera coastline with turquoise Ionian Sea, dramatic cliffs and hidden Mediterranean beaches — Ksamil, Himarë and Saranda region',
  },
  {
    icon: Landmark,
    title: 'UNESCO World Heritage',
    description: 'Three UNESCO sites including Berat\'s "City of a Thousand Windows," Gjirokastër\'s Ottoman stone city, and Butrint\'s 2,500-year archaeological park spanning Greek, Roman, and Byzantine civilizations.',
    image: 'https://d64gsuwffb70l.cloudfront.net/69cbf6b38dc4f4af5d55476d_1776963125104_57440e36.jpg',
    alt: 'UNESCO World Heritage Ottoman architecture in Berat and Gjirokastër, Albania — City of a Thousand Windows and stone city',
  },
  {
    icon: Mountain,
    title: 'The Accursed Mountains',
    description: 'The Albanian Alps offer some of Europe\'s most dramatic and untouched mountain scenery. The Theth-to-Valbona trail is consistently rated one of the world\'s greatest hikes.',
    image: 'https://d64gsuwffb70l.cloudfront.net/69cbf6b38dc4f4af5d55476d_1776963143323_5560b7cf.jpg',
    alt: 'The Accursed Mountains (Albanian Alps) with dramatic peaks and alpine valleys — Theth to Valbona legendary hiking trail',
  },
  {
    icon: Utensils,
    title: 'Culinary Heritage',
    description: 'A Mediterranean culinary tradition blending Ottoman, Italian, and Greek influences. Farm-to-table dining, organic olive oils, wild honey, and wines from ancient vineyards dating back millennia.',
    image: 'https://d64gsuwffb70l.cloudfront.net/69cbf6b38dc4f4af5d55476d_1776963168851_772c5dbd.jpg',
    alt: 'Traditional Albanian Mediterranean cuisine — farm-to-table dining, olive oil, wild honey and wines from ancient Albanian vineyards',
  },
  {
    icon: Sun,
    title: '300 Days of Sunshine',
    description: 'Albania enjoys a Mediterranean climate with over 300 days of sunshine annually. Warm summers perfect for coastal exploration and mild winters ideal for cultural immersion.',
    image: 'https://d64gsuwffb70l.cloudfront.net/69cbf6b38dc4f4af5d55476d_1776963217854_c1b15057.png',
    alt: 'Golden sunset over the Albanian Riviera — Albania enjoys over 300 days of Mediterranean sunshine per year',
  },
  {
    icon: Heart,
    title: 'Legendary Hospitality',
    description: 'The Albanian tradition of "Besa" — a sacred code of honor — means guests are treated as gifts from God. Experience warmth and generosity that transforms every encounter into a lasting memory.',
    image: 'https://d64gsuwffb70l.cloudfront.net/69cbf6b38dc4f4af5d55476d_1776963236908_16c4aeb3.jpg',
    alt: 'Traditional Albanian hospitality and Besa culture — welcoming guests in a heritage stone village in Albania',
  },
];

const reasons = [
  'Europe\'s last undiscovered Mediterranean gem',
  'Incredibly affordable luxury compared to neighboring countries',
  'Rich tapestry of Greek, Roman, Ottoman, and Italian influences',
  'Some of the cleanest beaches in the Mediterranean',
  'Vibrant, evolving culinary scene with organic traditions',
  'Warm, welcoming people with a deep tradition of hospitality',
  'Dramatic landscapes from coast to alpine peaks within hours',
  'Growing luxury infrastructure with boutique hotels and private villas',
];

const regionCards = [
  {
    name: 'The Albanian Riviera',
    desc: 'Crystal waters, hidden coves, and the finest beaches in the Mediterranean',
    image: 'https://d64gsuwffb70l.cloudfront.net/69cbf6b38dc4f4af5d55476d_1776963107484_b7256427.png',
    count: '12 Destinations',
    alt: 'The Albanian Riviera — Ksamil, Himarë and Saranda beaches along the Ionian Sea, Albania luxury travel',
  },
  {
    name: 'The Albanian Alps',
    desc: 'Dramatic peaks, pristine valleys, and legendary hiking trails',
    image: 'https://d64gsuwffb70l.cloudfront.net/69cbf6b38dc4f4af5d55476d_1776963311492_0d7e2c26.png',
    count: '8 Destinations',
    alt: 'The Albanian Alps (Accursed Mountains) — Theth and Valbona valleys, alpine hiking in northern Albania',
  },
  {
    name: 'Heritage Cities',
    desc: 'UNESCO sites, Ottoman architecture, and living history',
    image: 'https://d64gsuwffb70l.cloudfront.net/69cbf6b38dc4f4af5d55476d_1776963236908_16c4aeb3.jpg',
    count: '6 Destinations',
    alt: 'Albanian heritage cities — Berat, Gjirokastër and Butrint UNESCO World Heritage Ottoman and ancient sites',
  },
  {
    name: 'Lakes & Nature',
    desc: 'Ancient lakes, natural springs, and untouched wilderness',
    image: 'https://d64gsuwffb70l.cloudfront.net/69cbf6b38dc4f4af5d55476d_1776963355289_49d2518e.png',
    count: '10 Destinations',
    alt: 'Albanian lakes and nature — Lake Shkodër, Lake Ohrid, Blue Eye Spring and untouched wilderness',
  },
];

const AlbanianTravel: React.FC = () => {
  const navigate = useNavigate();
  const [activeHighlight, setActiveHighlight] = useState(0);

  return (
    <div className="min-h-screen bg-[#FAF8F3]">
      <Navbar />

      <PageHero
        image="https://d64gsuwffb70l.cloudfront.net/69cbf6b38dc4f4af5d55476d_1776963052710_47bee337.png"
        title={"Discover\nAlbania"}
        subtitle="Europe's last secret — where ancient heritage meets pristine Mediterranean shores and legendary hospitality awaits at every turn."
        badge="Albanian Travel"
        height="h-screen"
        imageAlt="Sweeping aerial view of the Albanian Riviera and Mediterranean coast — luxury Albania travel with Zera"
      />

      {/* Curated Itineraries — moved to the top, mirroring the Romania hub */}
      <CuratedItineraries
        onInquire={() => navigate('/booking')}
        items={albaniaItineraries}
        bookingLink={ALBANIA_BOOKING_LINK}
      />
      {/* Why Albania Section */}

      <section className="py-20 lg:py-32">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-[1px] bg-[#C9A961]" />
                <span className="font-sans text-[10px] tracking-[0.4em] uppercase text-[#C9A961]">Why Albania</span>
              </div>
              <h2 className="font-serif text-3xl md:text-5xl text-[#1A3B5C] font-light leading-tight mb-6">
                The Mediterranean's<br />Best-Kept Secret
              </h2>
              <p className="font-sans text-sm text-[#666] leading-relaxed mb-8">
                While the world flocks to the well-trodden paths of Greece, Croatia, and Italy, Albania quietly offers everything they have — and more. Pristine beaches without the crowds, UNESCO heritage sites without the queues, and a warmth of welcome that has been perfected over millennia.
              </p>
              <ul className="space-y-3 mb-10">
                {reasons.map((reason, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <Star className="w-3.5 h-3.5 text-[#C9A961] mt-0.5 flex-shrink-0" />
                    <span className="font-sans text-sm text-[#555]">{reason}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => navigate('/booking')}
                className="font-sans text-[11px] tracking-[0.25em] uppercase px-10 py-4 bg-[#1A3B5C] text-white hover:bg-[#2a5580] transition-all duration-300"
              >
                Plan Your Albanian Journey
              </button>
            </div>
            <div className="relative">
              <img
                src="https://d64gsuwffb70l.cloudfront.net/69cbf6b38dc4f4af5d55476d_1776963265999_69e2ac79.png"
                alt="Dramatic Albanian Riviera coastline with cliffs meeting the turquoise Ionian Sea — 450km of pristine Mediterranean beaches in Albania"
                className="w-full aspect-[4/5] object-cover"
                loading="lazy"
              />
              <div className="absolute -bottom-6 -left-6 bg-[#1A3B5C] text-white p-8 max-w-xs">
                <span className="font-serif text-4xl text-[#C9A961]">450km</span>
                <p className="font-sans text-xs text-white/60 mt-2">of pristine Mediterranean coastline to explore</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Highlights Grid */}
      <section className="py-20 lg:py-32 bg-[#0D1B2A]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-4 mb-6">
              <div className="w-12 h-[1px] bg-[#C9A961]" />
              <span className="font-sans text-[10px] tracking-[0.4em] uppercase text-[#C9A961]">What Makes Albania Special</span>
              <div className="w-12 h-[1px] bg-[#C9A961]" />
            </div>
            <h2 className="font-serif text-3xl md:text-5xl text-white font-light">
              Six Reasons to Fall in Love
            </h2>
          </div>

          {/* Featured Highlight */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 mb-12">
            <div className="relative aspect-[4/3] lg:aspect-auto overflow-hidden">
              <img
                src={highlights[activeHighlight].image}
                alt={highlights[activeHighlight].alt}
                className="w-full h-full object-cover transition-all duration-700"
                loading="lazy"
              />
            </div>
            <div className="bg-[#1A3B5C] p-10 lg:p-16 flex flex-col justify-center">
              {React.createElement(highlights[activeHighlight].icon, { className: 'w-8 h-8 text-[#C9A961] mb-6' })}
              <h3 className="font-serif text-2xl md:text-3xl text-white font-light mb-4">
                {highlights[activeHighlight].title}
              </h3>
              <p className="font-sans text-sm text-white/60 leading-relaxed mb-8">
                {highlights[activeHighlight].description}
              </p>
              <button
                onClick={() => navigate('/booking')}
                className="self-start font-sans text-[10px] tracking-[0.2em] uppercase text-[#C9A961] flex items-center gap-2 hover:gap-3 transition-all"
              >
                Explore More <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Highlight Tabs */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {highlights.map((h, i) => (
              <button
                key={i}
                onClick={() => setActiveHighlight(i)}
                className={`p-4 text-center transition-all duration-300 border ${
                  i === activeHighlight
                    ? 'border-[#C9A961] bg-[#C9A961]/10'
                    : 'border-white/10 hover:border-white/30'
                }`}
              >
                {React.createElement(h.icon, {
                  className: `w-5 h-5 mx-auto mb-2 ${i === activeHighlight ? 'text-[#C9A961]' : 'text-white/40'}`,
                })}
                <span className={`font-sans text-[9px] tracking-[0.15em] uppercase ${
                  i === activeHighlight ? 'text-[#C9A961]' : 'text-white/40'
                }`}>
                  {h.title.split(' ').slice(0, 2).join(' ')}
                </span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Regions of Albania */}
      <section className="py-20 lg:py-32">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <span className="font-sans text-[10px] tracking-[0.4em] uppercase text-[#C9A961]">Explore by Region</span>
            <h2 className="font-serif text-3xl md:text-5xl text-[#1A3B5C] font-light mt-4">
              Four Distinct Worlds
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {regionCards.map((region) => (
              <button
                key={region.name}
                onClick={() => navigate('/')}
                className="group relative aspect-[16/9] overflow-hidden"
              >
                <img
                  src={region.image}
                  alt={region.alt}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-8 text-left">
                  <span className="font-sans text-[9px] tracking-[0.2em] uppercase text-[#C9A961]">{region.count}</span>
                  <h3 className="font-serif text-2xl text-white font-light mt-1">{region.name}</h3>
                  <p className="font-sans text-xs text-white/60 mt-2">{region.desc}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Zera's Preferred Partners */}
      <section className="py-20 lg:py-28 bg-[#FAF8F3]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="text-center mb-14">
            <div className="flex items-center justify-center gap-4 mb-6">
              <div className="w-12 h-[1px] bg-[#C9A961]" />
              <span className="font-sans text-[10px] tracking-[0.4em] uppercase text-[#C9A961]">In Partnership With</span>
              <div className="w-12 h-[1px] bg-[#C9A961]" />
            </div>
            <h2 className="font-serif text-3xl md:text-5xl text-[#1A3B5C] font-light leading-tight">
              Zera's Preferred Partners
            </h2>
            <p className="font-sans text-sm text-[#666] leading-relaxed max-w-2xl mx-auto mt-6">
              Through our trusted relationships with the world's most distinguished hospitality brands, our guests enjoy exclusive privileges, priority access, and curated experiences across Albania and beyond.
            </p>
          </div>
          <div className="bg-[#F3EFE6] border border-[#C9A961]/20 px-6 md:px-12 py-10 md:py-14">
            <img
              src="https://d64gsuwffb70l.cloudfront.net/69c20284ac08f4efe2afa592_1779388875899_649f1ffa.png"
              alt="Zera's Preferred Partners — Four Seasons Preferred Partner, Small Luxury Hotels of the World, Belmond Bellini Club, Rosewood Elite, The Leading Hotels of the World, Virtuoso Member, Rocco Forte Knights, Relais & Châteaux, Diamond Club Dorchester Collection, The Peninsula PenClub, Aman, IHG Hotels & Resorts"
              className="w-full h-auto mx-auto max-w-5xl"
              loading="lazy"
            />
          </div>
        </div>
      </section>


      {/* CTA Section */}
      <section className="relative py-32">
        <img
          src="https://d64gsuwffb70l.cloudfront.net/69cbf6b38dc4f4af5d55476d_1776963374389_3a69e0ca.jpg"
          alt="Aerial view of the Albanian coastline at sunset — begin your bespoke Albania luxury travel journey with Zera"
          className="absolute inset-0 w-full h-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative z-10 text-center px-6">
          <h2 className="font-serif text-3xl md:text-5xl text-white font-light mb-4">Begin Your Albanian Story</h2>
          <p className="font-sans text-sm text-white/60 max-w-lg mx-auto mb-10">
            Let our concierge team craft a bespoke Albanian journey tailored to your every desire.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button onClick={() => navigate('/booking')} className="font-sans text-[11px] tracking-[0.25em] uppercase px-10 py-4 bg-[#C9A961] text-white hover:bg-[#b8944d] transition-all">
              Book Now
            </button>
            <button onClick={() => navigate('/blog')} className="font-sans text-[11px] tracking-[0.25em] uppercase px-10 py-4 border border-white/50 text-white hover:bg-white/10 transition-all">
              Travel Journal
            </button>
          </div>
        </div>
      </section>

      <ZeraPartners />
      <Footer />
    </div>
  );
};

export default AlbanianTravel;
